import { bus } from '@/main';
import { mapActions, mapGetters } from 'vuex';

export default {
  data() {
    return {
      pageOccurrencesIndex: 0,
      currentDocIndex: 0,
      currentDocPageIndex: 0,
    };
  },
  computed: {
    ...mapGetters('opportunityDocuments', ['opportunityDocuments', 'opportunityDocument']),
    ...mapGetters('currentDocument', ['currentPageNumber']),
    ...mapGetters('opportunityKeywordSearch', ['keywordSwitching']),
    ...mapGetters('pdfData', ['renderingCompleted']),
  },
  methods: {
    ...mapActions('opportunityKeywordSearch', [
      'incrementKeywordIndex',
      'decrementKeywordIndex',
      'setKeywordSwitching',
    ]),
    scrollKeywordIntoView(element) {
      const container = element.closest('.pdf-viewer');
      if (!container) return;
      const elementRect = element.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      container.scrollTop += elementRect.top - containerRect.top - container.clientHeight / 2 + elementRect.height / 2;
    },
    firstOccurrence({ keyword, locations }) {
      if (this.keywordSwitching) return;
      this.setKeywordSwitching(true);
      this.pageOccurrencesIndex = 0;
      this.currentDocIndex = 0;
      this.currentDocPageIndex = 0;

      if (!locations || !locations.length) {
        this.$toast.show(this.$t('page.analysis.keyword-navigator.no-occurrences'));
        this.setKeywordSwitching(false);
        return;
      }

      const firstDocumentId = locations[0].docId;

      if (this.opportunityDocument.id !== firstDocumentId) {
        this.switchDocument(firstDocumentId);
      }

      // We already are on the right page
      if (this.currentPageNumber === locations[0].pages[0]) {
        const keywordDivs = document.querySelectorAll(`.keyword-${keyword.id}-0`);
        if (keywordDivs.length > 0) {
          this.scrollKeywordIntoView(keywordDivs[0]);
          this.highlightCurrentOccurrence(keywordDivs);
        } else {
          console.warn(
            `No keyword divs found for keyword-${JSON.stringify(keyword)} on page ${this.currentPageNumber}`,
          );
        }
        this.setKeywordSwitching(false);
      } else {
        const unwatch = this.$watch(
          () => ({
            renderingCompleted: this.renderingCompleted,
          }),
          ({ renderingCompleted }) => {
            if (renderingCompleted && firstDocumentId) {
              this.$nextTick(() => {
                const page = locations[0].pages[0];
                if (page !== null && page !== undefined) {
                  bus.$emit('scroll-to-keyword-page', {
                    pageNumber: page,
                    callback: () => {
                      const keywordDivs = document.querySelectorAll(`.keyword-${keyword.id}-0`);
                      if (keywordDivs.length) {
                        this.scrollKeywordIntoView(keywordDivs[0]);
                        this.highlightCurrentOccurrence(keywordDivs);
                      }
                      this.setKeywordSwitching(false);
                      // Unwatch after the first triggering
                      unwatch();
                    },
                  });
                } else {
                  this.setKeywordSwitching(false);
                }
              });
            } else {
              this.setKeywordSwitching(false);
            }
          },
          { immediate: true },
        );
      }
    },
    nextOccurrence({ keyword, locations }) {
      this.goToOccurrence({ nextOccurrence: true, keyword, locations });
    },
    previousOccurrence({ keyword, locations }) {
      this.goToOccurrence({ nextOccurrence: false, keyword, locations });
    },
    goToOccurrence({ nextOccurrence, keyword, locations }) {
      if (this.keywordSwitching) return;
      this.setKeywordSwitching(true);

      this.lowlightKeywords(keyword);

      if (!locations || !locations.length) {
        this.setKeywordSwitching(false);
        return;
      }

      // Check for next page within the same document
      this.currentDocPageIndex = nextOccurrence ? this.currentDocPageIndex + 1 : this.currentDocPageIndex - 1;
      const currentLocation = locations[this.currentDocIndex];
      let targetPage = currentLocation.pages[this.currentDocPageIndex];
      if (targetPage && this.opportunityDocument.id === currentLocation.docId) {
        const onScrollCallback = () => {
          // get the page occurrence index
          if (nextOccurrence) {
            const prevPage = this.currentDocPageIndex > 0 ? currentLocation.pages[this.currentDocPageIndex - 1] : -1;
            this.pageOccurrencesIndex = targetPage === prevPage ? this.pageOccurrencesIndex + 1 : 0;
          } else {
            const nextPage =
              this.currentDocPageIndex < currentLocation.pages.length - 1
                ? currentLocation.pages[this.currentDocPageIndex + 1]
                : -1;
            this.pageOccurrencesIndex =
              nextPage === targetPage
                ? this.pageOccurrencesIndex - 1
                : this.getLargestPreviousKeyword(targetPage, keyword);
          }

          // Gather all the keyword parts from the page
          const pageKeywords = document
            .getElementById(`page-${targetPage}`)
            ?.querySelectorAll(`.keyword-${keyword.id}-${this.pageOccurrencesIndex}`);
          // Scroll and highlight the occurrence
          if (pageKeywords?.length > 0) {
            const firstOcc = pageKeywords[0];
            this.scrollKeywordIntoView(firstOcc);
            this.highlightCurrentOccurrence(pageKeywords);
          }
          if (nextOccurrence) {
            this.incrementKeywordIndex();
          } else {
            this.decrementKeywordIndex();
          }
          this.setKeywordSwitching(false);
        };
        if (targetPage === this.currentPageNumber) {
          onScrollCallback();
        } else {
          bus.$emit('scroll-to-keyword-page', {
            pageNumber: targetPage,
            callback: onScrollCallback,
          });
        }
      }
      // Else, Check occurrence page in the other documents...
      else {
        if (nextOccurrence) {
          this.nextDocumentOccurrence({ keyword, locations });
        } else {
          this.previousDocumentOccurrence({ keyword, locations });
        }
      }
    },
    nextDocumentOccurrence({ keyword, locations }) {
      // If there is no next document, clamp the index back and re-highlight current occurrence
      if (this.currentDocIndex === locations.length - 1) {
        this.currentDocPageIndex = locations[this.currentDocIndex].pages.length - 1;
        const page = locations[this.currentDocIndex].pages[this.currentDocPageIndex];
        const keywordDivs = document
          .getElementById(`page-${page}`)
          ?.querySelectorAll(`.keyword-${keyword.id}-${this.pageOccurrencesIndex}`);
        if (keywordDivs?.length) {
          this.highlightCurrentOccurrence(keywordDivs);
        }
        this.setKeywordSwitching(false);
        return;
      }

      const nextDocumentId = locations[this.currentDocIndex + 1].docId;
      if (!nextDocumentId) return;

      this.currentDocIndex++;
      this.currentDocPageIndex = 0;
      this.switchDocument(nextDocumentId);
      this.pageOccurrencesIndex = 0;

      const unwatch = this.$watch(
        () => ({
          renderingCompleted: this.renderingCompleted,
        }),
        ({ renderingCompleted }) => {
          if (!renderingCompleted) {
            return;
          }
          this.$nextTick(() => {
            const page = locations[this.currentDocIndex].pages[0];
            bus.$emit('scroll-to-keyword-page', {
              pageNumber: page,
              callback: () => {
                const foundedKeyword = document.querySelectorAll(`.keyword-${keyword.id}-0`);
                if (foundedKeyword.length) {
                  this.scrollKeywordIntoView(foundedKeyword[0]);
                  this.highlightCurrentOccurrence(foundedKeyword);
                }
                this.incrementKeywordIndex();
                this.setKeywordSwitching(false);
                // Unwatch after the first triggering
                unwatch();
              },
            });
          });
        },
        { immediate: true },
      );
    },
    previousDocumentOccurrence({ keyword, locations }) {
      // If there is no previous document, clamp the index back and re-highlight current occurrence
      if (this.currentDocIndex === 0) {
        this.currentDocPageIndex = 0;
        const page = locations[0].pages[0];
        const keywordDivs = document
          .getElementById(`page-${page}`)
          ?.querySelectorAll(`.keyword-${keyword.id}-${this.pageOccurrencesIndex}`);
        if (keywordDivs?.length) {
          this.highlightCurrentOccurrence(keywordDivs);
        }
        this.setKeywordSwitching(false);
        return;
      }
      const previousDocumentId = locations[this.currentDocIndex - 1].docId;
      if (!previousDocumentId) return;

      this.currentDocIndex -= 1;
      this.switchDocument(previousDocumentId);
      const unwatch = this.$watch(
        () => ({
          renderingCompleted: this.renderingCompleted,
        }),
        ({ renderingCompleted }) => {
          if (renderingCompleted) {
            this.$nextTick(() => {
              const location = locations[this.currentDocIndex];
              this.currentDocPageIndex = location.pages.length - 1;
              const previousPage = location.pages[this.currentDocPageIndex];
              bus.$emit('scroll-to-keyword-page', {
                pageNumber: previousPage,
                callback: () => {
                  this.pageOccurrencesIndex = this.getLargestPreviousKeyword(previousPage, keyword);

                  // Gather all the keyword parts from the page
                  const pageKeywords = document
                    .getElementById(`page-${previousPage}`)
                    ?.querySelectorAll(`.keyword-${keyword.id}-${this.pageOccurrencesIndex}`);

                  // Scroll and higlight the occurence
                  if (pageKeywords?.length > 0) {
                    const firstOcc = pageKeywords[0];
                    this.scrollKeywordIntoView(firstOcc);
                    this.highlightCurrentOccurrence(pageKeywords);
                  }
                  this.decrementKeywordIndex();
                  this.setKeywordSwitching(false);

                  // Unwatch after the first triggering
                  unwatch();
                },
              });
            });
          }
        },
        { immediate: true },
      );
    },
    getLargestPreviousKeyword(page, keyword) {
      const pageElement = document.getElementById(`page-${page}`);
      if (!pageElement) return 0;
      const pageKeywords = pageElement.querySelectorAll(`[class*="keyword-${keyword.id}"]`);
      if (!pageKeywords.length) return 0;

      // Find the largest index (.keyword-{id}-{index}) in the previous page. This is the last keyword
      const indexes = [];
      pageKeywords.forEach((keywordNode) => {
        const kwClass = [...keywordNode.classList].find((className) => className.startsWith('keyword-'));
        if (kwClass) indexes.push(parseInt(kwClass.split('-').pop(), 10));
      });
      return indexes.length ? Math.max(...indexes) : 0;
    },
    switchDocument(documentId) {
      const doc = this.opportunityDocuments.find((d) => d.id === documentId);
      if (!doc) return;
      bus.$emit('change-current-document', doc);
    },
    highlightCurrentOccurrence(keywordDivs) {
      this.lowlightKeywords();
      keywordDivs.forEach((subKeyword) => {
        subKeyword.style.filter = 'brightness(1)';
      });
    },
    lowlightKeywords() {
      const keywordDivs = document.querySelectorAll(`keyword`);
      keywordDivs.forEach((div) => {
        div.style.filter = 'brightness(0.3)';
      });
    },
    resetHighlightKeywords() {
      const keywordDivs = document.querySelectorAll(`keyword`);
      keywordDivs.forEach((div) => {
        div.style.filter = 'brightness(1)';
      });
    },
  },
};
